import React, { useState, useEffect, useRef } from "react";
import {
  Play,
  Square,
  Download,
  Copy,
  Check,
  ChevronDown,
  Volume2,
  Lock,
  Loader2,
  RotateCcw
} from "lucide-react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TokenBalanceCard from "../TokenBalanceCard";
import PlanStatusCard from "../PlanStatusCard";
import TranslationFeedback from "../TranslationFeedback";
import BetaModelBanner from "./BetaModelBanner";
import { getSubscription } from "../../../services/paymentService";
import { getApiKeys, getApiLogs } from "../../../services/developerService";
import { getUserById } from "../../../services/authService";
import { API_BASE_URL } from "../../../constant/constant";
import { generateSpeech, getVoices } from "../../../services/speechService";
import { estimateTextTokens } from "../../../utils/tokenCalculator";
import { LANGUAGES } from "../../../constant/constant";

const MAX_TTS_CHARACTERS = 600;

// Mock waveform bar heights for playback visualization
const WAVEFORM_BARS = [
  18, 32, 24, 40, 28, 52, 36, 20, 44, 30, 56, 38, 22, 48, 34, 26, 50, 32, 42, 24,
  54, 28, 46, 38, 20, 44, 30, 52, 36, 22, 48, 34, 26, 50, 32, 42, 24, 54, 28, 46,
  36, 20, 44, 30, 52, 36, 22, 48, 34, 26, 50, 32, 16, 14, 20, 30, 18, 22, 26, 20,
  30, 14, 18, 24, 28, 20, 16, 22, 18, 30, 14, 24, 20, 16, 28, 22, 18, 30, 14, 24,
];

// Helper to format dates to time-ago strings
const formatTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - date) / 1000);
  let interval = Math.floor(seconds / 31536000);

  if (interval >= 1) return interval === 1 ? '1 year ago' : `${interval} years ago`;
  interval = Math.floor(seconds / 2592000);
  if (interval >= 1) return interval === 1 ? '1 month ago' : `${interval} months ago`;
  interval = Math.floor(seconds / 86400);
  if (interval >= 1) return interval === 1 ? '1 day ago' : `${interval} days ago`;
  interval = Math.floor(seconds / 3600);
  if (interval >= 1) return interval === 1 ? '1 hour ago' : `${interval} hours ago`;
  interval = Math.floor(seconds / 60);
  if (interval >= 1) return interval === 1 ? '1 minute ago' : `${interval} minutes ago`;
  return seconds < 10 ? 'just now' : `${Math.floor(seconds)} seconds ago`;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const LanguageDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl text-sm text-gray-800 font-medium bg-white hover:border-gray-300 transition-colors"
      >
        <span>{value}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 z-20 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-48 overflow-y-auto">
          {LANGUAGES.map((lang) => (
            <button
              key={lang}
              onClick={() => { onChange(lang); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${value === lang
                ? "bg-[#F0FAF6] text-[#2D7A5F] font-semibold"
                : "text-gray-700 hover:bg-gray-50"
                }`}
            >
              {lang}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const VoiceOption = ({ voice, selected, onSelect }) => {
  const voiceValue = voice.value || voice.id;
  const voiceLabel = voice.label || voiceValue;
  return (
    <button
      type="button"
      onClick={() => onSelect(voiceValue)}
      className={`text-left px-4 py-3 rounded-xl border-2 transition-all duration-200 ${selected
        ? "border-[#2D7A5F] bg-[#F0FAF6]/40 shadow-sm"
        : "border-gray-200 bg-white hover:border-gray-300"
        }`}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-900">{voiceLabel}</p>
        {voice.is_default && (
          <span className="text-[10px] bg-[#2D7A5F]/10 text-[#2D7A5F] px-1.5 py-0.5 rounded font-medium">
            Default
          </span>
        )}
      </div>
      <p className="text-xs text-gray-500 mt-1 font-mono">{voiceValue}</p>
    </button>
  );
};

const Waveform = ({ isPlaying, progress }) => (
  <div className="flex items-end gap-[2px] h-14 w-full">
    {WAVEFORM_BARS.map((h, i) => {
      const filled = i / WAVEFORM_BARS.length < progress;
      return (
        <div
          key={i}
          className={`flex-1 rounded-sm transition-all ${filled ? "bg-white" : "bg-white/30"
            }`}
          style={{
            height: `${h}px`,
            ...(isPlaying && filled
              ? {
                animationName: "wavePulse",
                animationDuration: `${0.3 + (i % 5) * 0.1}s`,
                animationIterationCount: "infinite",
                animationDirection: "alternate",
                animationTimingFunction: "ease-in-out",
              }
              : {}),
          }}
        />
      );
    })}
    <style>{`
      @keyframes wavePulse {
        from { transform: scaleY(0.6); }
        to   { transform: scaleY(1); }
      }
    `}</style>
  </div>
);

const StatCell = ({ label, value }) => (
  <div>
    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-xl font-bold text-gray-900">{value}</p>
  </div>
);

const ActionButton = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
  >
    <Icon className="w-4 h-4" />
    {label}
  </button>
);

const HistoryItem = ({ item }) => (
  <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-900">{item.title}</p>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs text-gray-500">{item.lang}</span>
        <span className="text-gray-300 text-xs">•</span>
        <span className="text-xs text-gray-500">
          Tokens used:{" "}
          <span className="font-semibold text-gray-700">{item.tokens}</span>
        </span>
        <span className="text-gray-300 text-xs">•</span>
        <span className="text-xs text-gray-500">{item.voice}</span>
      </div>
    </div>
    <span className="text-xs text-gray-400 flex-shrink-0 ml-4">{item.time}</span>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const TextToSpeech = () => {
  const { token } = useSelector((state) => state.auth);

  // Subscription & User Details State
  const [checkingData, setCheckingData] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [remainingDays, setRemainingDays] = useState(null);
  const [apiKey, setApiKey] = useState(null);

  // TTS Config & Playback State
  const [text, setText] = useState("");
  const [language, setLanguage] = useState("English");
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [voicesError, setVoicesError] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState("en-US-Neural2-F");
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [copiedApi, setCopiedApi] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const [stats, setStats] = useState({
    processingTime: "0.00s",
    tokensConsumed: 0,
    duration: "0.0s",
    sampleRate: "24 kHz"
  });

  const [history, setHistory] = useState([]);
  const audioRef = useRef(null);

  const charCount = text.length;
  const isOverCharLimit = charCount > MAX_TTS_CHARACTERS;
  const estimatedTokens = estimateTextTokens(text);

  const handleTextChange = (e) => {
    const nextValue = e.target.value;
    if (nextValue.length > MAX_TTS_CHARACTERS) {
      setText(nextValue.slice(0, MAX_TTS_CHARACTERS));
      return;
    }
    setText(nextValue);
  };

  const handleTextPaste = (e) => {
    const pasted = e.clipboardData?.getData("text") ?? "";
    const selectionStart = e.target.selectionStart ?? text.length;
    const selectionEnd = e.target.selectionEnd ?? text.length;
    const nextLength = text.length - (selectionEnd - selectionStart) + pasted.length;
    if (nextLength > MAX_TTS_CHARACTERS) {
      toast.error(`Text is limited to ${MAX_TTS_CHARACTERS} characters.`);
    }
  };

  // Retrieve details and history
  const fetchDashboardData = async () => {
    if (!token) return;
    try {
      // 1. User details
      const userRes = await getUserById(token);
      if (userRes) {
        setUserDetails(userRes);

        // Calculate remaining subscription/token days
        if (userRes.tokenExpiration) {
          const now = new Date();
          const exp = new Date(userRes.tokenExpiration);
          const diffTime = exp.getTime() - now.getTime();
          const diffDays = Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
          setRemainingDays(diffDays);
        }
      }

      // 2. Subscription status
      const subResponse = await getSubscription(token);
      if (subResponse.success && subResponse.subscription) {
        setSubscriptionDetails(subResponse.subscription);
      }

      // 3. API Keys
      const keysResponse = await getApiKeys(token);
      const activeKey = keysResponse.apiKeys?.find(key => key.isActive);
      if (activeKey) {
        setApiKey(activeKey.key);
      }

      // 4. API Logs for history
      const logsResponse = await getApiLogs({ limit: 20 }, token);
      if (logsResponse?.logs) {
        const ttsLogs = logsResponse.logs.filter(
          log => (log.service && log.service.toUpperCase() === 'TTS') || (log.endpoint && log.endpoint.includes('/tts/'))
        );
        const mappedHistory = ttsLogs.map((log) => {
          const actionParts = (log.action || '').split(' • ');

          const voice = log.voice_name || log.successDetails?.requestData?.voice_name || 'Default voice';
          const time = log.when || (log.createdAt ? formatTimeAgo(new Date(log.createdAt)) : '');

          let lang = log.language || 'English';
          if (actionParts[0]) {
            const langMatch = actionParts[0].match(/^(.*?)\s+to\s+Speech/i);
            if (langMatch && langMatch[1]) {
              lang = langMatch[1].charAt(0).toUpperCase() + langMatch[1].slice(1).toLowerCase();
            }
          } else if (log.endpoint) {
            const match = log.endpoint.match(/\/tts\/v1\/([^/]+)/);
            if (match && match[1]) {
              lang = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
            }
          }

          const textInput = actionParts[1] || log.input || '';
          const wordsCount = textInput ? textInput.split(/\s+/).filter(Boolean).length : 0;
          const title = wordsCount > 0 ? `Generated speech (${wordsCount} words)` : 'Generated speech';
          const tokens = log.token ?? log.tokensUsed ?? 0;

          return {
            title,
            lang,
            tokens,
            voice,
            time
          };
        });
        setHistory(mappedHistory);
      }

    } catch (error) {
      console.error("Error loading dashboard details for TTS:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      setCheckingData(true);
      await fetchDashboardData();
      setCheckingData(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchVoices = async (lang) => {
    setLoadingVoices(true);
    setVoicesError(null);
    try {
      const data = await getVoices(lang, token);
      const fetchedVoices = data.voices || [];
      setVoices(fetchedVoices);

      let defaultVoiceVal = data.default;
      if (!defaultVoiceVal && fetchedVoices.length > 0) {
        const def = fetchedVoices.find((v) => v.is_default);
        defaultVoiceVal = def ? def.value : fetchedVoices[0].value;
      }
      if (defaultVoiceVal) {
        setSelectedVoice(defaultVoiceVal);
      }
    } catch (err) {
      console.error(`Error fetching voices for ${lang}:`, err);
      setVoicesError(`Unable to load ${lang} voices`);
      setVoices([]);
    } finally {
      setLoadingVoices(false);
    }
  };

  useEffect(() => {
    fetchVoices(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Handle language change and fetch voices
  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    fetchVoices(lang);
  };

  const checkTokenStatus = () => {
    if (!userDetails) {
      return { expired: false, exhausted: true };
    }
    const tokensAvailable = userDetails.tokens !== undefined && userDetails.tokens !== null && userDetails.tokens > 0;
    const exhausted = !tokensAvailable;
    let expired = false;
    if (userDetails.tokenExpiration) {
      const expirationDate = new Date(userDetails.tokenExpiration);
      const currentDate = new Date();
      expired = currentDate > expirationDate;
    }
    return { expired, exhausted };
  };

  // Perform TTS Generation Call
  const handleGenerate = async () => {
    if (!text.trim() || loading) return;

    if (text.length > MAX_TTS_CHARACTERS) {
      toast.error(`Text must be ${MAX_TTS_CHARACTERS} characters or fewer.`);
      return;
    }

    const { expired, exhausted } = checkTokenStatus();
    if (expired) {
      toast.error("Your tokens have expired. Please purchase a plan or renew your subscription.");
      return;
    }
    if (exhausted) {
      toast.error("Insufficient tokens. Please purchase a top-up plan.");
      return;
    }

    setLoading(true);
    setAudioReady(false);
    setIsPlaying(false);
    setCurrentTime(0);

    // Clean up previous blob URL to avoid resource leaks
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl("");
    }

    const startTime = Date.now();

    try {
      // Generate speech using the shared service function
      const audioBlob = await generateSpeech(text, language, selectedVoice, token);
      const localUrl = URL.createObjectURL(audioBlob);

      setAudioUrl(localUrl);
      setAudioReady(true);
      setShowFeedback(true);

      const processingMs = Date.now() - startTime;
      setStats({
        processingTime: `${(processingMs / 1000).toFixed(2)}s`,
        tokensConsumed: estimatedTokens,
        duration: "Loading...", // Will be overwritten dynamically when audio metadata loads
        sampleRate: "24 kHz"
      });

      toast.success("Speech generated successfully!");

      // Refresh remaining tokens and history logs
      await fetchDashboardData();

    } catch (error) {
      console.error("TTS request error:", error);
      toast.error(error.message || "Failed to generate speech. Please check backend connection.");
    } finally {
      setLoading(false);
    }
  };

  // Audio Playback Event Handlers
  const handlePlayPause = () => {
    if (!audioUrl || !audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.error("Playback failed:", err);
      });
    }
  };

  const handleStop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleClear = () => {
    setText("");
    setShowFeedback(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setAudioReady(false);
    setAudioUrl(null);
    setCurrentTime(0);
    setDuration(0);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    const dur = audioRef.current.duration;
    const safeDur = Number.isFinite(dur) && dur > 0 ? dur : 0;
    setDuration(safeDur);
    setStats(prev => ({
      ...prev,
      duration: `${safeDur.toFixed(1)}s`
    }));
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Synchronize audio volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Download Trigger
  const handleDownloadFile = () => {
    if (!audioUrl) return;
    const a = document.createElement("a");
    a.href = audioUrl;
    a.download = `tts-output-${language.toLowerCase()}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("MP3 downloaded successfully!");
  };

  const handleCopyApi = () => {
    const curl = `curl -X 'POST' \\
  '${API_BASE_URL}/tts/v1/${language.toLowerCase()}/with-api-key' \\
  -H 'accept: application/json' \\
  -H 'Authorization: Bearer ${apiKey || 'YOUR_API_KEY_HERE'}' \\
  -H 'Content-Type: application/json' \\
  -d '{
  "text": ${JSON.stringify(text || "How are you today?")},
  "voice_name": "${selectedVoice}",
  "speaking_rate": 1
}'`;
    navigator.clipboard.writeText(curl);
    setCopiedApi(true);
    toast.success("Curl command request copied!");
    setTimeout(() => setCopiedApi(false), 2000);
  };

  const formatTimeDisplay = (timeSecs) => {
    const mins = Math.floor(timeSecs / 60);
    const secs = Math.round(timeSecs % 60);
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  const progressPercentage = duration ? (currentTime / duration) : 0;

  if (checkingData) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { expired: tokensExpired, exhausted: tokensExhausted } = checkTokenStatus();
  const lockUi = tokensExpired || tokensExhausted;

  if (lockUi) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[600px]">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 max-w-xl w-full text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Premium feature locked
          </h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            This Text-to-Speech tool is premium and requires an active subscription or valid token balance.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 mx-auto space-y-5">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HTML Audio element hidden ref */}
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />

      {/* ── Beta Models Banner ── */}
      <BetaModelBanner />

      {/* ── Row 1: Token Balance + Plan Status ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TokenBalanceCard
          userDetails={userDetails}
          remainingDays={remainingDays}
          subscriptionDetails={subscriptionDetails}
        />
        <PlanStatusCard
          subscriptionDetails={subscriptionDetails}
        />
      </div>

      {/* ── Row 2: Text Input + Language/Voice ── */}
      <div className="flex gap-4 flex-col lg:flex-row">
        {/* Text area */}
        <div className="w-full lg:w-1/2 bg-white border border-gray-200 rounded-2xl p-5 flex flex-col">
          <textarea
            value={text}
            onChange={handleTextChange}
            onPaste={handleTextPaste}
            maxLength={MAX_TTS_CHARACTERS}
            placeholder="Enter the text you would like to synthesize to speech..."
            className="flex-1 resize-none outline-none text-[15px] text-gray-800 leading-relaxed placeholder-gray-400 min-h-[140px]"
          />
          <div className="flex items-center justify-between gap-3 flex-wrap pt-4 mt-2 border-t border-gray-100">
            <span className={`text-sm ${charCount >= MAX_TTS_CHARACTERS ? "text-red-500 font-medium" : "text-gray-400"}`}>
              {charCount}/{MAX_TTS_CHARACTERS} characters
              {charCount > 0 ? ` • Cost: ${estimatedTokens} Tokens` : ""}
              {charCount >= MAX_TTS_CHARACTERS ? " • Limit reached" : ""}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleClear}
                disabled={!text && !audioReady}
                className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors bg-white"
                title="Clear input text and reset audio output"
              >
                <RotateCcw className="w-4 h-4 text-gray-500" />
                Clear
              </button>
              <button
                onClick={handleGenerate}
                disabled={!text.trim() || loading || isOverCharLimit}
                className="flex items-center gap-2 bg-[#2B6651] hover:bg-[#235443] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
              >
                {loading && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                Generate Speech
              </button>
            </div>
          </div>
        </div>

        {/* Language + Voice selector */}
        <div className="w-full lg:w-1/2 bg-white border border-gray-200 rounded-2xl p-5 space-y-5">
          {/* Source Language */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2">
              Language
            </p>
            <LanguageDropdown value={language} onChange={handleLanguageChange} />
          </div>

          {/* Voice */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
              Voice
            </p>
            {loadingVoices ? (
              <div className="flex items-center justify-center py-6 text-gray-400 gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-[#2D7A5F]" />
                <span className="text-xs font-medium">Loading available voices...</span>
              </div>
            ) : voicesError ? (
              <div className="text-xs text-red-500 py-3 px-4 bg-red-50 rounded-xl flex items-center justify-between border border-red-100">
                <span>{voicesError}</span>
                <button
                  type="button"
                  onClick={() => fetchVoices(language)}
                  className="underline text-red-700 font-semibold hover:text-red-800"
                >
                  Retry
                </button>
              </div>
            ) : voices.length === 0 ? (
              <p className="text-xs text-gray-400 py-3">No voices available for {language}.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-64 overflow-y-auto pr-1">
                {voices.map((voice) => (
                  <VoiceOption
                    key={voice.value || voice.id}
                    voice={voice}
                    selected={selectedVoice === (voice.value || voice.id)}
                    onSelect={setSelectedVoice}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Row 3: Audio Output ── */}
      {audioReady && (
        <div className="bg-[#2B6651] rounded-2xl p-5 shadow-sm">
          {/* Label + filename */}
          <p className="text-xs font-semibold text-[#7EC8A8] uppercase tracking-widest mb-1">
            Audio Output
          </p>
          <p className="text-white text-sm font-medium mb-4">tts-output.mp3</p>

          {/* Waveform */}
          <Waveform isPlaying={isPlaying} progress={progressPercentage} />

          {/* Controls row */}
          <div className="flex items-center gap-3 mt-4">
            {/* Play/Pause */}
            <button
              onClick={handlePlayPause}
              className="w-9 h-9 rounded-full bg-white flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              {isPlaying
                ? <div className="flex gap-[3px]"><div className="w-[3px] h-4 bg-[#2B6651] rounded-sm" /><div className="w-[3px] h-4 bg-[#2B6651] rounded-sm" /></div>
                : <Play className="w-4 h-4 text-[#2B6651] ml-0.5" />
              }
            </button>

            {/* Stop */}
            <button
              onClick={handleStop}
              className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors flex-shrink-0"
            >
              <Square className="w-3.5 h-3.5 text-white fill-white" />
            </button>

            {/* Progress track */}
            <div className="flex-1 flex items-center gap-3">
              <div
                className="flex-1 h-[3px] bg-white/30 rounded-full cursor-pointer relative"
                onClick={(e) => {
                  if (!audioRef.current || !duration) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const percent = clickX / rect.width;
                  audioRef.current.currentTime = percent * duration;
                  setCurrentTime(percent * duration);
                }}
              >
                <div
                  className="h-full bg-white rounded-full transition-all duration-100"
                  style={{ width: `${progressPercentage * 100}%` }}
                />
              </div>
            </div>

            {/* Timestamp */}
            <span className="text-white/70 text-xs font-mono whitespace-nowrap">
              {formatTimeDisplay(currentTime)} / {formatTimeDisplay(duration)}
            </span>

            {/* Divider */}
            <div className="w-px h-4 bg-white/30" />

            {/* Volume */}
            <Volume2 className="w-4 h-4 text-white/70 flex-shrink-0" />
            <div
              className="w-16 h-[3px] bg-white/30 rounded-full cursor-pointer relative"
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const nextVolume = Math.max(0, Math.min(1, clickX / rect.width));
                setVolume(nextVolume);
              }}
            >
              <div className="h-full bg-white rounded-full" style={{ width: `${volume * 100}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* ── Row 4: Stats + Actions ── */}
      {audioReady && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-5">
          {/* 4 stat cells */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCell label="Processing Time" value={stats.processingTime} />
            <StatCell label="Tokens Consumed" value={stats.tokensConsumed} />
            <StatCell label="Duration" value={stats.duration} />
            <StatCell label="Sample Rate" value={stats.sampleRate} />
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <ActionButton icon={Download} label="Download MP3" onClick={handleDownloadFile} />
            <ActionButton
              icon={copiedApi ? Check : Copy}
              label="Copy API Request"
              onClick={handleCopyApi}
            />
          </div>
        </div>
      )}

      {/* ── Translation Feedback Card ── */}
      {audioReady && showFeedback && (
        <TranslationFeedback
          input={text}
          output="Text-to-speech audio generated"
          fromLanguage="Text"
          toLanguage={language}
          apiEndpoint={`/tts/v1/${language.toLowerCase()}`}
          onHide={() => setShowFeedback(false)}
        />
      )}

      {/* ── Row 5: Generation History ── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Generation History</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">No text-to-speech recordings generated yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {history.map((item, i) => (
              <HistoryItem key={i} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TextToSpeech;
